import { mentalHealthRetriggerRecDefnId } from '@/utils/constants';
import { isNumber } from '@/utils/math';
import { RbcStatus, UsercryptCheckinEntry } from '@calm/evexia-web-sdk';
import { useLatestCheckinEntries } from '@rhg/hooks/use-latest-checkin-entries';
import resultsMessages from '@rhg/lang/messages/results';
import { CheckinTypeKey, classifyResult } from '@rhg/utils/usercrypt/checkins';
import { useIntl } from 'react-intl';
import { useCheckinEntriesByType } from '../use-checkin-entries-by-type';
import useLocalSelfHarmScore from '../use-local-self-harm-score';
import { useActiveCircleRecommendations } from '../use-recommendations';
import { CheckinResult, CheckinResultItem } from './types';

type FormatMessage = ReturnType<typeof useIntl>['formatMessage'];

const createEntryWithTranslations = (
    entryType: CheckinTypeKey,
    entry: UsercryptCheckinEntry,
    formatMessage: FormatMessage
): CheckinResultItem => {
    const classification =
        entryType === CheckinTypeKey.SelfHarm
            ? classifyResult({ selfHarmEntry: entry })
            : entryType === CheckinTypeKey.Gad
            ? classifyResult({ gadEntry: entry })
            : classifyResult({
                  phqEntry: entry,
                  useModerateSevere: true,
              });
    return {
        entry,
        score: entry.checkins.values[0].value ?? 0,
        classification,
        label: formatMessage(
            resultsMessages[`results.label.${classification}`]
        ),
    };
};

const createResultWithTranslations = (
    formatMessage: FormatMessage,
    gadEntry: UsercryptCheckinEntry,
    phqEntry: UsercryptCheckinEntry,
    selfHarmEntry?: UsercryptCheckinEntry,
    hideSelfHarmClassification?: boolean
): CheckinResult => {
    const resultClassification = classifyResult({
        gadEntry,
        phqEntry,
        selfHarmEntry,
        hideSelfHarmClassification,
    });
    return {
        gad: createEntryWithTranslations(
            CheckinTypeKey.Gad,
            gadEntry,
            formatMessage
        ),
        phq: createEntryWithTranslations(
            CheckinTypeKey.Phq,
            phqEntry,
            formatMessage
        ),
        ...(selfHarmEntry
            ? {
                  selfHarm: createEntryWithTranslations(
                      CheckinTypeKey.SelfHarm,
                      selfHarmEntry,
                      formatMessage
                  ),
              }
            : {}),
        resultClassification,
        resultClassificationLabel: formatMessage(
            resultsMessages[`results.label.${resultClassification}`]
        ),
    };
};

export function useScreeningResults() {
    const { formatMessage } = useIntl();

    const { latestCheckinEntries, isFetched: isLatestEntriesFetched } =
        useLatestCheckinEntries([
            CheckinTypeKey.Gad,
            CheckinTypeKey.Phq,
            CheckinTypeKey.SelfHarm,
        ]);

    const gadLatestEntry = latestCheckinEntries[CheckinTypeKey.Gad];
    const phqLatestEntry = latestCheckinEntries[CheckinTypeKey.Phq];
    const selfHarmLatestEntry = latestCheckinEntries[CheckinTypeKey.SelfHarm];
    const {
        checkinEntries: gadCheckins,
        // meta: gadMeta,
        isFetched: areAllGadCheckinsFetched,
    } = useCheckinEntriesByType(CheckinTypeKey.Gad);
    const {
        checkinEntries: phqCheckins,
        // meta: phqMeta,
        isFetched: areAllPhqCheckinsFetched,
    } = useCheckinEntriesByType(CheckinTypeKey.Phq);
    const {
        checkinEntries: allSelfHarmCheckins,
        // meta: selfHarmMeta,
        isFetched: areAllSelfHarmCheckinsFetched,
    } = useCheckinEntriesByType(CheckinTypeKey.SelfHarm);
    const [localSelfHarmScore] = useLocalSelfHarmScore();

    const isFetched =
        isLatestEntriesFetched &&
        areAllGadCheckinsFetched &&
        areAllPhqCheckinsFetched &&
        areAllSelfHarmCheckinsFetched;

    // TODO - wrapper to allow translations to be passed through to a non-hook. Ideally we remove the need the translation aspect of this hook
    const createEntry = (
        entryType: CheckinTypeKey,
        entry: UsercryptCheckinEntry
    ) => {
        return createEntryWithTranslations(entryType, entry, formatMessage);
    };

    const latestResult =
        gadLatestEntry?.latestEntry && phqLatestEntry?.latestEntry
            ? createResultWithTranslations(
                  formatMessage,
                  gadLatestEntry?.latestEntry,
                  phqLatestEntry?.latestEntry,
                  selfHarmLatestEntry?.latestEntry,
                  !localSelfHarmScore
              )
            : undefined;

    const hasMultipleResults =
        (gadCheckins?.length ?? 0) > 1 ||
        (phqCheckins?.length ?? 0) > 1 ||
        (allSelfHarmCheckins?.length ?? 0) > 1;

    return {
        latestResult,
        hasMultipleResults,
        isFetched,
        createEntry,
        gadCheckins,
        phqCheckins,
    };
}

export function useScreeningStatus() {
    const { polls, isFetched: areRecommendationsFetched } =
        useActiveCircleRecommendations();
    const { latestResult, isFetched: areScreeningResultsFetched } =
        useScreeningResults();

    const isRetriggered = polls?.some(
        (poll) =>
            poll.recDefnId === mentalHealthRetriggerRecDefnId &&
            poll.status !== RbcStatus.Declined
    );
    const hasTakenPoll =
        !!latestResult &&
        isNumber(latestResult.gad.score) &&
        isNumber(latestResult.phq.score);

    return {
        hasTakenPoll,
        isRetriggered: Boolean(isRetriggered),
        isFetched: areRecommendationsFetched && areScreeningResultsFetched,
    };
}
